import { useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIIllustration from 'UIComponents/image/UIIllustration';
import Categories from 'ContentUtils/constants/Categories';
import useContentForContentManagementPanel from 'ContentEditorUI/hooks/useContentForContentManagementPanel';
import useBlogEmptyStateData from 'ContentEditorUI/hooks/useBlogEmptyStateData';
import ContentManagementSideNav from './ContentManagementSidenav';
import ContentSearchInput from './ContentSearchInput';
import ContentTable from './ContentTable';
import ContentTablePaginator from './ContentTablePaginator';
import ContentActionsModalContainer from './ContentActionsModalContainer';
import { ContentZeroState, BlogZeroState } from './ContentZeroStates';
import ContentManagementSidebarHeader from './ContentManagementSidebarHeader';
import ContentUsageLimitsProvider from './ContentUsageLimitsProvider';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Box } from 'foundations-components/transitional/components/Box';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const NoSearchResultsState = () => /*#__PURE__*/_jsx(Flex, {
  flexDirection: "column",
  justifyContent: "center",
  blockSize: "75%",
  children: /*#__PURE__*/_jsxs(Text, {
    as: "span",
    textAlign: "center",
    "data-test-id": "content-management-panel-empty-state",
    children: [/*#__PURE__*/_jsx(UIIllustration, {
      name: "empty-state-charts"
    }), /*#__PURE__*/_jsx(View, {
      marginBlock: "100",
      paddingInline: "1100",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "contentManagementPanel.table.emptyState"
      })
    })]
  })
});
const ContentTableContainer = ({
  data,
  page,
  pageCount,
  updateExcludedContentIdsFilter,
  sorts,
  total,
  onPaginate,
  updateSort,
  searchQuery,
  shouldShowNowEditingTableRow
}) => {
  const shouldShowPaginator = total !== 0 && pageCount > 1;
  const shouldShowNoSearchResultsState = total === 0 && searchQuery;
  const [modalData, setModalData] = useState({
    openModalName: '',
    content: null,
    modalOptions: {}
  });
  const openModal = (modalName, content, modalOptions = {}) => {
    setModalData({
      openModalName: modalName,
      content,
      modalOptions
    });
  };
  const closeModal = () => {
    setModalData({
      openModalName: '',
      content: null,
      modalOptions: {}
    });
  };
  return /*#__PURE__*/_jsxs(Box, {
    grow: 1,
    maxBlockSize:
    // calculation uses constant values from sass: calc(100% - #{$sidebar-header-height} -  #{$ui-search-bar-height} - 30px)
    shouldShowPaginator ? 'calc(100% - 90px - 72px - 30px)' : 'calc(100% - 90px - 72px)',
    inlineSize: "100%",
    children: [shouldShowNoSearchResultsState ? /*#__PURE__*/_jsx(NoSearchResultsState, {}) : /*#__PURE__*/_jsx(ContentTable, {
      data: data,
      openModal: openModal,
      sorts: sorts,
      updateSort: updateSort,
      shouldShowNowEditingTableRow: shouldShowNowEditingTableRow
    }), shouldShowPaginator && /*#__PURE__*/_jsx(ContentTablePaginator, {
      page: page,
      pageCount: pageCount,
      onPaginate: onPaginate
    }), /*#__PURE__*/_jsx(ContentActionsModalContainer, {
      closeModal: closeModal,
      content: modalData.content,
      openModalName: modalData.openModalName,
      modalOptions: modalData.modalOptions,
      updateExcludedContentIdsFilter: updateExcludedContentIdsFilter
    })]
  });
};
const ContentManagement = ({
  hideSideNav = false
}) => {
  const {
    fetchContent,
    updateExcludedContentIdsFilter,
    updateCurrentContentTypeId,
    updateSearch,
    data,
    contentTypeId,
    stale,
    requestStatus,
    query,
    sorts,
    updateSort,
    onPaginate,
    page,
    pageCount,
    total,
    shouldShowNowEditingTableRow
  } = useContentForContentManagementPanel();
  const {
    hasNoBlogs,
    hasNoBlogInCurrentDomain,
    createBlogPost,
    createBlogListingPage
  } = useBlogEmptyStateData();
  const shouldShowBlogZeroState = [Categories.blog_post.id, Categories.blog_listing_page.id].includes(contentTypeId) && (hasNoBlogs || hasNoBlogInCurrentDomain);
  const shouldShowContentZeroState = requestStatus === 'SUCCEEDED' && total === 0 && !query && !shouldShowNowEditingTableRow;
  useEffect(() => {
    if (requestStatus === 'UNINITIALIZED' || stale) {
      fetchContent();
    }
  }, [fetchContent, stale, requestStatus]);
  return /*#__PURE__*/_jsxs(ContentUsageLimitsProvider, {
    children: [/*#__PURE__*/_jsx(ContentManagementSidebarHeader, {
      contentTypeId: contentTypeId
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      blockSize: "100%",
      children: [!hideSideNav && /*#__PURE__*/_jsx(View, {
        minInlineSize: "175px",
        maxInlineSize: "175px",
        children: /*#__PURE__*/_jsx(ContentManagementSideNav, {
          currentlySelectedContentTypeId: contentTypeId,
          updateCurrentContentTypeId: updateCurrentContentTypeId
        })
      }), /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        blockSize: "100%",
        inlineSize: "100%",
        paddingInline: "300",
        borderInlineStartColor: "border-core-subtle",
        borderInlineStartWidth: "100",
        borderInlineStartStyle: !hideSideNav ? 'solid' : 'none',
        children: shouldShowBlogZeroState ? /*#__PURE__*/_jsx(BlogZeroState, {}) : shouldShowContentZeroState ? /*#__PURE__*/_jsx(ContentZeroState, {
          contentTypeId: contentTypeId,
          createBlogPost: createBlogPost,
          createBlogListingPage: createBlogListingPage
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ContentSearchInput, {
            contentTypeId: contentTypeId,
            search: query,
            updateSearch: updateSearch
          }), requestStatus === 'PENDING' ? /*#__PURE__*/_jsx(UILoadingSpinner, {
            "data-test-id": "content-table-loading",
            minHeight: 300,
            layout: "centered"
          }) : /*#__PURE__*/_jsx(ContentTableContainer, {
            data: data,
            onPaginate: onPaginate,
            page: page,
            pageCount: pageCount,
            updateExcludedContentIdsFilter: updateExcludedContentIdsFilter,
            sorts: sorts,
            total: total,
            updateSort: updateSort,
            searchQuery: query,
            shouldShowNowEditingTableRow: shouldShowNowEditingTableRow
          })]
        })
      })]
    })]
  });
};
export default ContentManagement;